import httpClient from '~/lib/http-client';
import type { PaymentMethodSummary, InvoiceSummary } from '~/lib/api/billing';
import type { CancelReason } from '@dunea/shared';

/*
 * Dunea's plans — Free, Plus, Pro and Max — on the ONE credit balance the website,
 * Dunea Desktop and Dunea Work share.
 *
 * They are served and sold by the desktop billing routes (/api/desktop/billing/*):
 * the website buys the same Dunea Desktop products the app does, so a plan
 * bought here shows up in the app and the other way round. Website-started
 * flows send `returnTo: 'web'` so Dodo returns here (`?checkout=success`,
 * `?credits=purchased`, `?billing=updated`) instead of the app's hand-off page.
 *
 * Every number on the plans screen comes from these responses. Nothing here
 * has a fallback price or allotment: if a call fails, the screen shows an error
 * and a Retry, never a guessed number.
 */

export type AccountPlanTier = 'free' | 'plus' | 'pro' | 'max';
export type PaidAccountPlanTier = Exclude<AccountPlanTier, 'free'>;
export type PlanInterval = 'month' | 'year';

/** Which product's plan refills the balance. 'web' = a grandfathered website plan. */
export type PlanProduct = 'web' | 'code' | 'work';

/** The plan behind the account's one balance (server: billing/account-balance.ts `accountPlanOf`). */
export interface AccountPlan {
  product: PlanProduct | null;
  /** that plan's tier in its own product ('growth', 'plus', …); 'free' on Free */
  tier: string;
  /** credits the plan refills each month; the one-time free grant on Free */
  creditsAllocated: number;
}

export interface LocalPlanPrice {
  value: number;
  currency: string;
}

/** One plan as the server sells it (server: payments/desktop-plans.ts). */
export interface AccountPlanOption {
  tier: AccountPlanTier;
  name: string;
  tagline: string;
  /** monthly price, USD */
  priceUsd: number;
  /** yearly price, USD; absent on Free and on Max, which is monthly only */
  priceUsdYear?: number;
  /** credits a month on paid plans; the one-time grant on Free */
  credits: number;
  period: 'once' | 'month';
  features: string[];
  badge?: string;
  /** the monthly price in the buyer's currency, when Dodo has one */
  localPrice?: LocalPlanPrice;
  localPriceYear?: LocalPlanPrice;
}

export interface AccountPlansResponse {
  currency: string;
  plans: AccountPlanOption[];
}

/** The desktop-family billing overview (server: payments/desktop-billing.types.ts). */
export interface AccountPlanOverview {
  tier: AccountPlanTier;
  status: 'none' | 'active' | 'cancelled' | 'past_due' | 'on_hold';
  creditsAllocated: number;
  creditsRemaining: number;
  plan: AccountPlan;
  purchasedCredits: number;
  canTopUp: boolean;
  billingEmail: string | null;
  interval: PlanInterval;
  /** the price per billing period (a year, on a yearly plan) */
  priceMonthly: number | null;
  currency: string;
  subscriptionId: string | null;
  currentPeriodStart: string | null;
  currentPeriodEnd: string | null;
  startedAt: string | null;
  cancelAtPeriodEnd: boolean;
  paymentMethod: PaymentMethodSummary | null;
  scheduledPlan: PaidAccountPlanTier | null;
  effectiveDate: string | null;
}

export interface PlanChangePreview {
  currentTier: AccountPlanTier;
  targetTier: PaidAccountPlanTier;
  interval: PlanInterval;
  kind: 'upgrade' | 'downgrade';
  currentPriceMonthly: number;
  newPriceMonthly: number;
  /** an estimate — Dodo bills the exact prorated amount */
  amountDueToday: number;
  creditsNow: number;
  effectiveDate: string | null;
  currency: string;
}

export interface PlanTopUpOptions {
  /** unrounded: credits = round(dollars × rate), exactly as the checkout computes it */
  creditsPerDollar: number;
  minUsd: number;
  maxUsd: number;
  quickPicks: { usd: number; credits: number }[];
}

/**
 * The desktop product whose routes manage a plan. Plans bought on the website
 * are Dunea Desktop plans ('code', the server's default); a plan bought in
 * Dunea Work is managed with Work's header.
 */
export type PlanApp = 'code' | 'work';

function appHeaders(app: PlanApp = 'code') {
  return app === 'work' ? { headers: { 'X-Dunea-App': 'work' } } : {};
}

/** The product that manages the account's plan, from the plan behind the balance. */
export function planAppOf(plan: AccountPlan | undefined | null): PlanApp {
  return plan?.product === 'work' ? 'work' : 'code';
}

export const ACCOUNT_PLANS_KEY = ['account-plans'] as const;
export const accountOverviewKey = (app: PlanApp) => ['account-plan-overview', app] as const;
export const accountTopUpKey = (app: PlanApp) => ['account-plan-topup', app] as const;

export async function apiGetAccountPlans(): Promise<AccountPlansResponse> {
  const { data } = await httpClient.get<AccountPlansResponse>('/api/desktop/billing/plans');
  return data;
}

export async function apiGetAccountPlanOverview(app: PlanApp = 'code'): Promise<AccountPlanOverview> {
  const { data } = await httpClient.get<AccountPlanOverview>('/api/desktop/billing/overview', appHeaders(app));
  return data;
}

/** Start Dodo's hosted checkout for Plus or Pro. A 400 carries the server's reason (e.g. a website plan is active). */
export async function apiStartPlanCheckout(
  tier: PaidAccountPlanTier,
  interval: PlanInterval,
): Promise<{ url: string }> {
  const { data } = await httpClient.post<{ url: string }>('/api/desktop/billing/checkout', {
    tier,
    interval,
    returnTo: 'web',
  });
  return data;
}

/** Stop (true) or resume (false) the plan at the end of the paid period. */
export async function apiSetPlanCancel(
  cancel: boolean,
  app: PlanApp = 'code',
  why?: { reason?: CancelReason; detail?: string },
): Promise<{ cancelAtPeriodEnd: boolean; currentPeriodEnd: string | null }> {
  const { data } = await httpClient.post<{ cancelAtPeriodEnd: boolean; currentPeriodEnd: string | null }>(
    '/api/desktop/billing/cancel',
    { cancel, ...why },
    appHeaders(app),
  );
  return data;
}

export async function apiPreviewPlanSwitch(
  tier: PaidAccountPlanTier,
  interval: PlanInterval,
  app: PlanApp = 'code',
): Promise<PlanChangePreview> {
  const { data } = await httpClient.get<PlanChangePreview>('/api/desktop/billing/change/preview', {
    params: { tier, interval },
    ...appHeaders(app),
  });
  return data;
}

/** Upgrade now (prorated) or schedule a downgrade for the period end. */
export async function apiSwitchPlan(
  tier: PaidAccountPlanTier,
  interval: PlanInterval,
  app: PlanApp = 'code',
): Promise<{ kind: 'upgraded' | 'scheduled'; effectiveDate: string | null }> {
  const { data } = await httpClient.post<{ kind: 'upgraded' | 'scheduled'; effectiveDate: string | null }>(
    '/api/desktop/billing/change',
    { tier, interval },
    appHeaders(app),
  );
  return data;
}

/** Withdraw a scheduled downgrade. */
export async function apiKeepPlan(app: PlanApp = 'code'): Promise<{ ok: true }> {
  const { data } = await httpClient.post<{ ok: true }>('/api/desktop/billing/keep', {}, appHeaders(app));
  return data;
}

/** Dodo's hosted card-update page for the plan; returns to the website. */
export async function apiPlanCardUpdate(app: PlanApp = 'code'): Promise<{ url: string }> {
  const { data } = await httpClient.post<{ url: string }>(
    '/api/desktop/billing/payment-method',
    { returnTo: 'web' },
    appHeaders(app),
  );
  return data;
}

export async function apiGetPlanInvoices(app: PlanApp = 'code'): Promise<InvoiceSummary[]> {
  const { data } = await httpClient.get<{ invoices: InvoiceSummary[] }>(
    '/api/desktop/billing/invoices',
    appHeaders(app),
  );
  return data.invoices;
}

/** The top-up rate ($1 = 10 credits on every plan today — read, never assumed). */
export async function apiGetPlanTopUpOptions(app: PlanApp = 'code'): Promise<PlanTopUpOptions> {
  const { data } = await httpClient.get<PlanTopUpOptions>('/api/desktop/billing/topup', appHeaders(app));
  return data;
}

export async function apiStartPlanTopUp(amountUsd: number, app: PlanApp = 'code'): Promise<{ url: string }> {
  const { data } = await httpClient.post<{ url: string }>(
    '/api/desktop/billing/topup',
    { amountUsd, returnTo: 'web' },
    appHeaders(app),
  );
  return data;
}
